import React, { Component, Suspense, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Html, Text } from "@react-three/drei";
import { useContextBridge } from "its-fine";
import * as THREE from "three";
import { clamp, constrainScenePosition, getNearbyIndices, movementStep, HEADER_BAND_HEIGHT, PANEL_HTML_WIDTH, PANEL_WORLD_PER_PIXEL } from "../../utils/muulti3dLayout";
import { getSurfaceColorProps, parseColor } from "../../utils/colorValue";
import { TexturePool } from "../../utils/muulti3dTexturePool";
import { drawSurfaceImage, getSurfaceTextureSize } from "../../utils/muulti3dSurface";
import { resolveTypography } from "../../utils/resolveTypography";

export const QUALITY_PRESETS = {
    low: { label: "Low", dprCap: 1, shadows: false, shadowMapSize: 512, textureSize: 512, viewDistance: 320, buildingLimit: 24, textureDistance: 140, textureLimit: 8, panelDistance: 28, panelLimit: 6 },
    medium: { label: "Medium", dprCap: 1.5, shadows: true, shadowMapSize: 1024, textureSize: 1024, viewDistance: 520, buildingLimit: 40, textureDistance: 200, textureLimit: 12, panelDistance: 40, panelLimit: 10 },
    high: { label: "High", dprCap: 2, shadows: true, shadowMapSize: 2048, textureSize: 2048, viewDistance: 800, buildingLimit: 64, textureDistance: 280, textureLimit: 16, panelDistance: 56, panelLimit: 14 },
};

const SPRINT_MIN = 2, SPRINT_MAX = 5, SPRINT_STEP = 0.5;
const SPRINT_STORAGE_KEY = "muulti-3d-sprint-speed";
function normalizeSprintMultiplier(value) {
    const number = Number(value);
    return Number.isFinite(number) ? clamp(Math.round(number / SPRINT_STEP) * SPRINT_STEP, SPRINT_MIN, SPRINT_MAX) : SPRINT_MAX;
}

export function useSceneQuality() {
    return useState("high");
}

// This browser preference follows the visitor between the street and indoor Spaces.
export function useSceneLighting() {
    const [lightingOn, setLightingOn] = useState(() => {
        try { return typeof window === "undefined" || window.localStorage.getItem("muulti-3d-lighting") !== "off"; }
        catch { return true; }
    });
    const updateLighting = useCallback(enabled => {
        setLightingOn(enabled);
        try { window.localStorage.setItem("muulti-3d-lighting", enabled ? "on" : "off"); }
        catch { /* Lighting remains usable when browser storage is unavailable. */ }
    }, []);
    return [lightingOn, updateLighting];
}

const textures = new TexturePool();
const jobs = [];
let activeJobs = 0;
function pumpTextures() {
    while (activeJobs < 2 && jobs.length) {
        activeJobs += 1;
        jobs.shift()();
    }
}
function queueTexture(create) {
    return new Promise((resolve, reject) => {
        jobs.push(() => Promise.resolve().then(create).then(resolve, reject).finally(() => {
            activeJobs -= 1;
            pumpTextures();
        }));
        pumpTextures();
    });
}

function canvasTexture(canvas) {
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 1;
    return texture;
}

function makeSurfaceTexture(surface, size, dimensions) {
    if (surface.imageUrl) {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => {
                try {
                    const width = dimensions ? dimensions[0] / PANEL_WORLD_PER_PIXEL : image.naturalWidth;
                    const height = dimensions ? dimensions[1] / PANEL_WORLD_PER_PIXEL : image.naturalHeight;
                    const canvas = document.createElement("canvas");
                    [canvas.width, canvas.height] = getSurfaceTextureSize(width, height, size);
                    const ctx = canvas.getContext("2d");
                    if (!ctx) throw new Error("Canvas textures are unavailable");
                    ctx.fillStyle = surface.color;
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    drawSurfaceImage(ctx, image, surface, width, height);
                    resolve(canvasTexture(canvas));
                } catch (error) {
                    reject(error);
                } finally {
                    image.onload = null;
                    image.onerror = null;
                }
            };
            image.onerror = () => { image.onload = null; image.onerror = null; reject(new Error("Image unavailable")); };
            image.src = surface.imageUrl;
        });
    }
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas textures are unavailable");
    const direction = String(surface.direction).toLowerCase();
    let dx = direction.includes("left") ? -1 : direction.includes("right") ? 1 : 0;
    let dy = direction.includes("top") ? -1 : direction.includes("bottom") ? 1 : 0;
    if (direction.endsWith("deg")) {
        const angle = parseFloat(direction) * Math.PI / 180;
        dx = Math.sin(angle);
        dy = -Math.cos(angle);
    }
    if (!dx && !dy) dx = 1;
    const gradient = ctx.createLinearGradient(128 - dx * 128, 128 - dy * 128, 128 + dx * 128, 128 + dy * 128);
    surface.colors.forEach((color, index) => gradient.addColorStop(index / Math.max(1, surface.colors.length - 1), color));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);
    return canvasTexture(canvas);
}

// Async maps change shader defines even when the material instance stays the same.
export function updateSurfaceMaterial(material) {
    material.needsUpdate = true;
}

export function useSurfaceTexture(surface, size, enabled = true, dimensions = null) {
    const key = JSON.stringify([surface.imageUrl, surface.colors, surface.direction, size, surface.color,
        surface.sizing, surface.position, surface.repeatMode, dimensions]);
    const [loaded, setLoaded] = useState(null);
    const needsTexture = !!surface.imageUrl || surface.colors.length > 0;
    useEffect(() => {
        if (!enabled || !needsTexture) return undefined;
        let cancelled = false;
        const [imageUrl, colors, direction, maxSize, color, sizing, position, repeatMode, wallSize] = JSON.parse(key);
        const lease = textures.acquire(key, () => queueTexture(() =>
            makeSurfaceTexture({ imageUrl, colors, direction, color, sizing, position, repeatMode }, maxSize, wallSize)));
        lease.promise.then(texture => {
            if (!cancelled) setLoaded({ key, texture });
        }).catch(() => { if (!cancelled) setLoaded(null); });
        return () => { cancelled = true; lease.release(); };
    }, [key, enabled, needsTexture]);
    return enabled && loaded?.key === key ? loaded.texture : null;
}

// Drei Html creates a separate DOM root. Forward the existing providers into it,
// including the current router, wallet and connection; do not create a new session.
export function SceneHtml({ children, ...props }) {
    const Bridge = useContextBridge();
    return <Html {...props}><Bridge>{children}</Bridge></Html>;
}

export function getScenePanelTextStyle(pageTypography, panelStyle = {}) {
    const typography = resolveTypography(pageTypography, panelStyle);
    const color = panelStyle?.color || typography.paragraph.color || "#000000";
    return {
        color, "--muulti-3d-panel-text": color,
        "--muulti-3d-panel-heading": typography.headers.color || color,
        "--muulti-3d-panel-link": typography.links.color || color,
    };
}

export function ScenePanelBody({ children, pageTypography, panelStyle }) {
    return <div className="muulti-3d-panel-content" style={getScenePanelTextStyle(pageTypography, panelStyle)}>{children}</div>;
}

// Measure CSS layout pixels, never perspective-transformed screen coordinates.
export function ScenePanelContent({ children, title, onOpen, onMeasure, pageTypography, panelStyle }) {
    const panelRef = useRef(null);
    useLayoutEffect(() => {
        const element = panelRef.current;
        let cancelled = false;
        let previousHeight = 0;
        const measure = () => {
            if (cancelled) return;
            const height = Math.ceil(Math.max(element.offsetHeight, element.scrollHeight));
            if (height > 0 && height !== previousHeight) {
                previousHeight = height;
                onMeasure(height);
            }
        };
        measure();
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
        observer?.observe(element);
        element.addEventListener("load", measure, true);
        element.addEventListener("loadedmetadata", measure, true);
        window.addEventListener("resize", measure);
        document.fonts?.ready.then(measure);
        return () => {
            cancelled = true;
            observer?.disconnect();
            element.removeEventListener("load", measure, true);
            element.removeEventListener("loadedmetadata", measure, true);
            window.removeEventListener("resize", measure);
        };
    }, [onMeasure]);
    return <div ref={panelRef} className="muulti-3d-space-panel" data-scene-ui="true" style={{ width: PANEL_HTML_WIDTH }}>
        <ScenePanelBody pageTypography={pageTypography} panelStyle={panelStyle}>{children}</ScenePanelBody>
        <button className="muulti-3d-expand" onClick={onOpen} aria-label={"Expand " + title}>Expand panel</button>
    </div>;
}

export function SceneWallBand({ half, height, surface, texture, inside = false }) {
    const edge = half + (inside ? -0.04 : 0.04);
    const y = height - HEADER_BAND_HEIGHT / 2;
    const walls = [
        { name: "front", position: [0, y, edge], yaw: 0 },
        { name: "right", position: [edge, y, 0], yaw: Math.PI / 2 },
        { name: "back", position: [0, y, -edge], yaw: Math.PI },
        { name: "left", position: [-edge, y, 0], yaw: -Math.PI / 2 },
    ];
    return walls.map(wall => <mesh key={wall.name} name={(inside ? "interior" : "exterior") + "-header-" + wall.name}
        position={wall.position} rotation-y={wall.yaw + (inside ? Math.PI : 0)}>
        <planeGeometry args={[half * 2, HEADER_BAND_HEIGHT]} />
        <meshBasicMaterial onUpdate={updateSurfaceMaterial} map={texture} {...getSurfaceColorProps(surface, texture)} />
    </mesh>);
}

export function SceneText(props) {
    const color = parseColor(props.color);
    return <Suspense fallback={null}><Text {...props} color={color?.hex || props.color}
        fillOpacity={(color?.alpha ?? 1) * (props.fillOpacity ?? 1)} /></Suspense>;
}

// Central proximity subscriptions, with React updates only when the set changes.
export function useNearbyIndices(items, playerPosRef, distance, limit) {
    const [indices, setIndices] = useState(() => getNearbyIndices(items, playerPosRef.current, distance, limit));
    const previous = useRef(indices.join(","));
    const elapsed = useRef(0.2);
    useFrame((_, dt) => {
        elapsed.current += dt;
        if (elapsed.current < 0.2) return;
        elapsed.current = 0;
        const next = getNearbyIndices(items, playerPosRef.current, distance, limit);
        const key = next.join(",");
        if (key !== previous.current) {
            previous.current = key;
            setIndices(next);
        }
    });
    return indices;
}

const isSceneUI = target => target instanceof Element &&
    !!target.closest("input, select, textarea, button, a, summary, [contenteditable], [data-scene-ui]");

export function useSceneInput(rootRef, paused, onSprintAdjust) {
    const keysRef = useRef({});
    const moveAxisRef = useRef({ x: 0, y: 0 });
    const lookAxisRef = useRef({ x: 0, y: 0 });
    const mouseRef = useRef({ x: 0, y: 0 });
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;
        let drag = null;
        const reset = () => {
            keysRef.current = {};
            moveAxisRef.current.x = moveAxisRef.current.y = 0;
            lookAxisRef.current.x = lookAxisRef.current.y = 0;
            mouseRef.current.x = mouseRef.current.y = 0;
            drag = null;
        };
        const keyName = event => event.key.length === 1 ? event.key.toLowerCase() : event.key;
        const onDown = event => {
            const key = keyName(event);
            if (paused || event.ctrlKey || event.metaKey || event.altKey || isSceneUI(event.target) || !root.contains(document.activeElement)) return;
            if (["w", "a", "s", "d", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Shift"].includes(key)) {
                keysRef.current[key] = true;
                if (event.shiftKey) keysRef.current.Shift = true;
                event.preventDefault();
            }
        };
        const onUp = event => { delete keysRef.current[keyName(event)]; };
        const onPointerDown = event => {
            if (paused || isSceneUI(event.target)) return;
            root.focus({ preventScroll: true });
            keysRef.current.Shift = !!event.shiftKey;
            if (event.pointerType === "mouse" && event.button === 0) {
                drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
            }
        };
        const onMove = event => {
            if (!drag || drag.id !== event.pointerId) return;
            mouseRef.current.x += event.clientX - drag.x;
            mouseRef.current.y += event.clientY - drag.y;
            drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
        };
        const onPointerUp = event => { if (drag?.id === event.pointerId) drag = null; };
        const onFocusOut = event => { if (!root.contains(event.relatedTarget) || isSceneUI(event.relatedTarget)) reset(); };
        const onVisibility = () => { if (document.hidden) reset(); };
        const onWheel = event => {
            const delta = event.deltaY || event.deltaX;
            if (paused || !onSprintAdjust || !delta || event.ctrlKey || event.metaKey || event.altKey ||
                !(event.shiftKey || keysRef.current.Shift) || isSceneUI(event.target) || !root.contains(document.activeElement)) return;
            event.preventDefault();
            onSprintAdjust(delta < 0 ? 1 : -1);
        };
        root.addEventListener("wheel", onWheel, { passive: false });
        root.addEventListener("pointerdown", onPointerDown);
        root.addEventListener("focusout", onFocusOut);
        window.addEventListener("keydown", onDown, { passive: false });
        window.addEventListener("keyup", onUp);
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onPointerUp);
        window.addEventListener("pointercancel", onPointerUp);
        window.addEventListener("blur", reset);
        document.addEventListener("visibilitychange", onVisibility);
        reset();
        return () => {
            reset();
            root.removeEventListener("wheel", onWheel);
            root.removeEventListener("pointerdown", onPointerDown);
            root.removeEventListener("focusout", onFocusOut);
            window.removeEventListener("keydown", onDown);
            window.removeEventListener("keyup", onUp);
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onPointerUp);
            window.removeEventListener("pointercancel", onPointerUp);
            window.removeEventListener("blur", reset);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [rootRef, paused, onSprintAdjust]);
    return { keysRef, moveAxisRef, lookAxisRef, mouseRef };
}

export function VirtualJoystick({ valueRef, deadzone = 0.08, label = "Move", side = "left", disabled = false }) {
    const baseRef = useRef(null);
    const knobRef = useRef(null);
    const pointerRef = useRef(null);
    const setAxis = useCallback((x, y) => {
        const length = Math.max(1, Math.hypot(x, y));
        x /= length;
        y /= length;
        if (Math.hypot(x, y) < deadzone) x = y = 0;
        valueRef.current.x = x;
        valueRef.current.y = y;
        if (knobRef.current) knobRef.current.style.transform = "translate(" + x * 28 + "px, " + y * 28 + "px)";
    }, [valueRef, deadzone]);
    const endDrag = useCallback(event => {
        if (event?.pointerId != null && event.pointerId !== pointerRef.current) return;
        const pointer = pointerRef.current;
        pointerRef.current = null;
        setAxis(0, 0);
        try {
            if (pointer != null && baseRef.current?.hasPointerCapture?.(pointer)) baseRef.current.releasePointerCapture(pointer);
        } catch { /* Pointer capture may already have ended. */ }
    }, [setAxis]);
    useEffect(() => {
        const reset = () => endDrag();
        window.addEventListener("blur", reset);
        document.addEventListener("visibilitychange", reset);
        window.addEventListener("pointerup", endDrag);
        window.addEventListener("pointercancel", endDrag);
        if (disabled) reset();
        return () => {
            window.removeEventListener("blur", reset);
            document.removeEventListener("visibilitychange", reset);
            window.removeEventListener("pointerup", endDrag);
            window.removeEventListener("pointercancel", endDrag);
            reset();
        };
    }, [disabled, endDrag]);
    const onPointerMove = event => {
        if (event.pointerId !== pointerRef.current) return;
        const rect = baseRef.current.getBoundingClientRect();
        setAxis((event.clientX - rect.left - rect.width / 2) / (rect.width / 2),
            (event.clientY - rect.top - rect.height / 2) / (rect.height / 2));
    };
    return (
        <div className={"muulti-joystick-wrap" + (side === "right" ? " is-right" : "")} aria-hidden="true" data-scene-ui="true">
            <div ref={baseRef} className="muulti-joystick-base"
                onPointerDown={event => {
                    if (disabled || pointerRef.current != null || (event.pointerType === "mouse" && event.button !== 0)) return;
                    event.preventDefault();
                    pointerRef.current = event.pointerId;
                    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Use window release fallback. */ }
                    onPointerMove(event);
                }}
                onPointerMove={onPointerMove} onPointerUp={endDrag}
                onPointerCancel={endDrag} onLostPointerCapture={endDrag}>
                <div ref={knobRef} className="muulti-joystick-knob" />
            </div>
            <div className="muulti-joystick-hint">{label}</div>
        </div>
    );
}

export function PlayerRig({ input, bounds, playerPosRef, poseRef, teleportRef, paused = false, sprintMultiplier = SPRINT_MAX }) {
    const { camera } = useThree();
    const yaw = useRef(poseRef.current.yaw || 0);
    const pitch = useRef(poseRef.current.pitch || 0);
    useFrame((_, dt) => {
        const position = playerPosRef.current;
        if (teleportRef?.current) {
            const pose = teleportRef.current;
            position.set(pose.x, 0, pose.z);
            yaw.current = pose.yaw || 0;
            pitch.current = pose.pitch || 0;
            teleportRef.current = null;
        }
        const keys = input.keysRef.current;
        const delta = Math.min(dt, 0.05);
        if (!paused) {
            yaw.current -= ((input.lookAxisRef.current.x + (keys.ArrowRight ? 0.85 : 0) - (keys.ArrowLeft ? 0.85 : 0)) * 2.2 * delta) + input.mouseRef.current.x * 0.003;
            pitch.current = clamp(pitch.current - ((input.lookAxisRef.current.y + (keys.ArrowDown ? 0.85 : 0) - (keys.ArrowUp ? 0.85 : 0)) * 2.2 * delta) - input.mouseRef.current.y * 0.003, -1.1, 1.1);
            const speed = 6 * (keys.Shift ? normalizeSprintMultiplier(sprintMultiplier) : 1);
            const step = movementStep(input.moveAxisRef.current.x + (keys.d ? 1 : 0) - (keys.a ? 1 : 0),
                input.moveAxisRef.current.y + (keys.s ? 1 : 0) - (keys.w ? 1 : 0), yaw.current, delta, speed);
            position.x += step.x;
            position.z += step.z;
        }
        input.mouseRef.current.x = input.mouseRef.current.y = 0;
        const constrained = constrainScenePosition(position, bounds);
        position.x = constrained.x;
        position.z = constrained.z;
        position.y = 0;
        camera.position.set(position.x, 1.75, position.z);
        camera.rotation.set(pitch.current, yaw.current, 0, "YXZ");
        Object.assign(poseRef.current, { x: position.x, z: position.z, yaw: yaw.current, pitch: pitch.current });
    });
    return null;
}

class SceneErrorBoundary extends Component {
    state = { failed: false };
    static getDerivedStateFromError() { return { failed: true }; }
    render() {
        if (this.state.failed) return <div className="muulti-3d-status" role="alert">
            <p>The 3D view is unavailable on this device.</p>
            <a className="muulti-button" href={this.props.fallbackHref}>Open the 2D view</a>
        </div>;
        return this.props.children;
    }
}

export function SceneViewport({ children, label, qualityKey, setQualityKey, bounds, playerPosRef, poseRef, teleportRef,
    paused = false, actions, overlay, fallbackHref = "/muulti-directory", showShadows = true, shadowsOn, setShadowsOn,
    lightingOn = true, setLightingOn }) {
    const rootRef = useRef(null);
    const [sprintMultiplier, setSprintMultiplier] = useState(() => {
        try { return normalizeSprintMultiplier(window.localStorage.getItem(SPRINT_STORAGE_KEY) ?? SPRINT_MAX); }
        catch { return SPRINT_MAX; }
    });
    const adjustSprint = useCallback(direction => {
        setSprintMultiplier(previous => normalizeSprintMultiplier(previous + direction * SPRINT_STEP));
    }, []);
    const input = useSceneInput(rootRef, paused, adjustSprint);
    const quality = QUALITY_PRESETS[qualityKey];
    useEffect(() => {
        try { window.localStorage.setItem(SPRINT_STORAGE_KEY, String(sprintMultiplier)); }
        catch { /* Sprint remains usable when browser storage is unavailable. */ }
    }, [sprintMultiplier]);
    useEffect(() => {
        const root = rootRef.current;
        const resize = () => {
            const height = window.visualViewport?.height || window.innerHeight;
            root.style.setProperty("--muulti-scene-height", Math.max(200, height - Math.max(0, root.getBoundingClientRect().top)) + "px");
        };
        resize();
        const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
        const navbar = document.querySelector(".navbar");
        if (navbar) observer?.observe(navbar);
        window.addEventListener("resize", resize);
        window.visualViewport?.addEventListener("resize", resize);
        return () => {
            observer?.disconnect();
            window.removeEventListener("resize", resize);
            window.visualViewport?.removeEventListener("resize", resize);
        };
    }, []);
    return (
        <div ref={rootRef} className="muulti-3d-root" role="region" tabIndex={0} aria-label={label}>
            <details className="muulti-3d-controls" data-scene-ui="true">
                <summary>Scene controls</summary>
                <div className="muulti-3d-settings">
                    <label>Quality<select value={qualityKey} onChange={event => setQualityKey(event.target.value)}>
                        {Object.entries(QUALITY_PRESETS).map(([key, preset]) => <option key={key} value={key}>{preset.label}</option>)}
                    </select></label>
                    <label>Sprint speed: {sprintMultiplier}x
                        <input type="range" aria-label="Sprint speed" min={SPRINT_MIN} max={SPRINT_MAX} step={SPRINT_STEP}
                            value={sprintMultiplier} aria-valuetext={sprintMultiplier + " times normal speed"}
                            onChange={event => setSprintMultiplier(normalizeSprintMultiplier(event.target.value))} />
                    </label>
                    <p className="muulti-3d-speed-hint">Hold Shift to sprint. Shift + mouse wheel adjusts speed.</p>
                    {setLightingOn && <label className="muulti-3d-toggle">
                        <input type="checkbox" checked={lightingOn} onChange={event => setLightingOn(event.target.checked)} />Lighting
                    </label>}
                    {showShadows && <label className="muulti-3d-toggle">
                        <input type="checkbox" checked={lightingOn && quality.shadows && shadowsOn} disabled={!lightingOn || !quality.shadows}
                            onChange={event => setShadowsOn(event.target.checked)} />Shadows
                    </label>}
                    {actions}
                </div>
            </details>
            <p className="muulti-3d-instructions">Click the scene to explore · WASD to move · arrows or drag to look</p>
            <SceneErrorBoundary fallbackHref={fallbackHref}>
                <Canvas shadows={lightingOn && showShadows && quality.shadows && shadowsOn}
                    dpr={[1, quality.dprCap]} gl={{ antialias: false }}
                    camera={{ position: [playerPosRef.current.x, 1.75, playerPosRef.current.z], fov: 65, near: 0.1, far: Math.max(250, quality.viewDistance + 60) }}
                    fallback={<div className="muulti-3d-status"><a href={fallbackHref}>Open the 2D view</a></div>}>
                    {children}
                    <PlayerRig input={input} bounds={bounds} playerPosRef={playerPosRef} poseRef={poseRef} teleportRef={teleportRef} paused={paused} sprintMultiplier={sprintMultiplier} />
                </Canvas>
            </SceneErrorBoundary>
            <VirtualJoystick valueRef={input.moveAxisRef} disabled={paused} label="Move" />
            <VirtualJoystick valueRef={input.lookAxisRef} disabled={paused} label="Look" side="right" deadzone={0.05} />
            {overlay}
        </div>
    );
}

export function SceneDialog({ title, onClose, children }) {
    const titleId = useId();
    const cardRef = useRef(null);
    const closeRef = useRef(null);
    useEffect(() => {
        const previousFocus = document.activeElement;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeRef.current?.focus();
        return () => {
            document.body.style.overflow = overflow;
            if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
        };
    }, []);
    return createPortal(
        <div className="muulti-3d-modal" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
            <div ref={cardRef} className="muulti-3d-modal-card" role="dialog" aria-modal="true" aria-labelledby={titleId}
                onKeyDown={event => {
                    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); onClose(); }
                    if (event.key !== "Tab") return;
                    const elements = Array.from(cardRef.current.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'));
                    const first = elements[0], last = elements[elements.length - 1];
                    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
                    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
                }}>
                <div className="muulti-3d-dialog-heading">
                    <h2 id={titleId}>{title}</h2><button ref={closeRef} className="muulti-button" onClick={onClose}>Close</button>
                </div>
                {children}
            </div>
        </div>, document.body
    );
}
